import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface DailyTrendsChartProps {
  data: any[] | null | undefined;
}

const DailyTrendsChart: React.FC<DailyTrendsChartProps> = ({ data }) => {
  // Transform data for line chart
  const chartData = React.useMemo(() => {
    // Ensure data is an array before processing
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }

    // Group data by date and sum up totals by status
    const groupedData: { [key: string]: any } = {};
    
    data.forEach(item => {
      const date = item.trend_date || item.trend_month; // Handle both daily and monthly data
      if (!groupedData[date]) {
        groupedData[date] = {
          date,
          PUBLISHED: 0,
          FAILED: 0,
          INVALID_PROPERTY: 0,
          FILENAME_INVALID: 0,
          COMPLETED: 0,
          OTHER: 0
        };
      }
      
      const status = item.status?.toUpperCase() || 'UNKNOWN';
      if (groupedData[date][status] !== undefined) {
        groupedData[date][status] += item.total_requests || 0;
      } else {
        // For any status not in our predefined list, add to OTHER
        groupedData[date].OTHER += item.total_requests || 0;
      }
    });

    // Convert to array and sort by date
    return Object.values(groupedData).sort((a: any, b: any) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }, [data]);

  // Determine if this is monthly data by checking the first item
  const isMonthlyData = React.useMemo(() => {
    if (!data || data.length === 0) {
      return false;
    }
    // Check if the data has trend_month property or if trend_date format suggests monthly data (YYYY-MM)
    return data[0].trend_month || (data[0].trend_date && data[0].trend_date.length === 7);
  }, [data]);

  const formatDate = (dateString: string) => {
    if (isMonthlyData) {
      // For monthly data, format as "Jan 2025"
      return new Date(dateString + '-01').toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short'
      });
    }
    // For daily data, format as "Jan 15"
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="label">{`${isMonthlyData ? 'Month' : 'Date'}: ${formatDate(label)}`}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.dataKey}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (!chartData || chartData.length === 0) {
    return (
      <div className="chart-container">
        <h3>{isMonthlyData ? 'Monthly Trends' : 'Daily Trends'}</h3>
        <div className="no-data">
          <p>No trend data available for the selected period.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <h3>{isMonthlyData ? 'Monthly Trends' : 'Daily Trends'}</h3>
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="date" 
            tickFormatter={formatDate}
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="PUBLISHED" 
            stroke="#00C49F" 
            strokeWidth={2}
            dot={{ r: 4 }}
            name="Published"
          />
          <Line 
            type="monotone" 
            dataKey="FAILED" 
            stroke="#FF0000" 
            strokeWidth={2}
            dot={{ r: 4 }}
            name="Failed"
          />
          <Line 
            type="monotone" 
            dataKey="INVALID_PROPERTY" 
            stroke="#FFBB28" 
            strokeWidth={2}
            dot={{ r: 4 }}
            name="Invalid Property"
          />
          <Line 
            type="monotone" 
            dataKey="FILENAME_INVALID" 
            stroke="#FF8042" 
            strokeWidth={2}
            dot={{ r: 4 }}
            name="Filename Invalid"
          />
          <Line 
            type="monotone" 
            dataKey="COMPLETED" 
            stroke="#8884D8" 
            strokeWidth={2}
            dot={{ r: 4 }}
            name="Completed"
          />
          <Line 
            type="monotone" 
            dataKey="OTHER" 
            stroke="#FFA500" 
            strokeWidth={2}
            dot={{ r: 4 }}
            name="Other"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DailyTrendsChart;
